import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, UserRound } from 'lucide-react';

type SidebarProfileMenuProps = {
  isCollapsed: boolean;
  displayName: string;
  email: string;
  avatar: React.ReactNode;
  /** When omitted, the menu only offers Sign out (e.g. admin with no profile page yet). */
  profilePath?: string;
  onSignOut: () => void | Promise<void>;
  /** Optional content below the chip (e.g. coach Upgrade). */
  belowMenu?: React.ReactNode;
};

const SidebarProfileMenu: React.FC<SidebarProfileMenuProps> = ({
  isCollapsed,
  displayName,
  email,
  avatar,
  profilePath,
  onSignOut,
  belowMenu,
}) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  const panel = open ? (
    <div
      className={
        isCollapsed
          ? 'fixed bottom-24 left-16 z-50 w-64 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg'
          : 'absolute bottom-full left-0 right-0 z-50 mb-2 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg'
      }
      role="menu"
      aria-label="Profile menu"
    >
      <div className="border-b border-gray-100 px-3 py-2.5">
        <p className="truncate text-sm font-semibold text-gray-900">{displayName}</p>
        <p className="mt-0.5 truncate text-xs text-gray-500">{email}</p>
      </div>
      <div className="p-1">
        {profilePath ? (
          <Link
            to={profilePath}
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <UserRound className="h-4 w-4 text-gray-500" aria-hidden />
            View profile
          </Link>
        ) : null}
        <button
          type="button"
          role="menuitem"
          data-testid="sign-out"
          onClick={() => {
            setOpen(false);
            void onSignOut();
          }}
          className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
        >
          <LogOut className="h-4 w-4 text-gray-500" aria-hidden />
          Sign out
        </button>
      </div>
    </div>
  ) : null;

  if (isCollapsed) {
    return (
      <div ref={menuRef} className="relative flex flex-col items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen((current) => !current)}
          aria-label="Profile menu"
          aria-expanded={open}
          aria-haspopup="menu"
          className="rounded-full hover:opacity-90 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-rowing-blue focus-visible:ring-offset-2"
        >
          {avatar}
        </button>
        {panel}
        {belowMenu}
      </div>
    );
  }

  return (
    <div ref={menuRef} className="relative space-y-2">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-label="Profile menu"
        aria-expanded={open}
        aria-haspopup="menu"
        className="flex w-full items-center rounded-lg p-2 text-left transition-colors hover:bg-gray-50"
      >
        {avatar}
        <div className="ml-3 min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-gray-900">{displayName}</p>
          <p className="truncate text-xs text-gray-500">{email}</p>
        </div>
      </button>
      {panel}
      {belowMenu}
    </div>
  );
};

export default SidebarProfileMenu;
